'use client';

import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import Modal from '@/components/ui/Modal';
import { Check } from 'lucide-react';

interface Props {
  open: boolean;
  onClose: () => void;
}

interface CampaignFormData {
  name: string;
  client: string;
  objective: string;
  platform: string;
  budget: string;
  budgetType: string;
  startDate: string;
  endDate: string;
  targetAge: string;
  targetGender: string;
  targetLocations: string;
  targetInterests: string;
  adCopy: string;
  ctaButton: string;
  destinationUrl: string;
}

const steps = [
  { id: 'step-details', label: 'Campaign Details' },
  { id: 'step-targeting', label: 'Ad Set & Targeting' },
  { id: 'step-creative', label: 'Creative & Copy' },
  { id: 'step-review', label: 'Review & Launch' },
];

const clients = ['Bravo Coffee', 'Nexus Tech', 'Verde Organic', 'Luxe Living', 'UrbanWear Co.', 'FitTrack Pro', 'Solaris Solar', 'PetPal Supply'];

export default function CampaignCreateModal({ open, onClose }: Props) {
  const [step, setStep] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { register, handleSubmit, watch, formState: { errors } } = useForm<CampaignFormData>({
    defaultValues: { budgetType: 'daily', platform: 'both', targetGender: 'all' },
  });

  const watchedValues = watch();

  const onSubmit = async () => {
    setIsSubmitting(true);
    // Backend integration point: POST /api/campaigns/create
    await new Promise((r) => setTimeout(r, 1500));
    toast.success('Campaign created and submitted for review');
    setIsSubmitting(false);
    onClose();
    setStep(0);
  };

  const nextStep = () => setStep((s) => Math.min(steps.length - 1, s + 1));
  const prevStep = () => setStep((s) => Math.max(0, s - 1));

  return (
    <Modal open={open} onClose={onClose} title="Create New Campaign" subtitle="Complete all steps to launch your campaign" size="lg">
      {/* Step Progress */}
      <div className="px-6 py-4 border-b border-border">
        <div className="flex items-center gap-0">
          {steps.map((s, i) => (
            <React.Fragment key={s.id}>
              <div className="flex items-center gap-2">
                <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold transition-all duration-200 ${
                  i < step ? 'bg-positive text-white' : i === step ? 'bg-primary text-white' : 'bg-muted text-muted-foreground'
                }`}>
                  {i < step ? <Check size={13} /> : i + 1}
                </div>
                <span className={`text-xs font-medium hidden sm:block ${i === step ? 'text-foreground' : 'text-muted-foreground'}`}>
                  {s.label}
                </span>
              </div>
              {i < steps.length - 1 && (
                <div className={`flex-1 h-0.5 mx-2 transition-all duration-300 ${i < step ? 'bg-positive' : 'bg-border'}`} />
              )}
            </React.Fragment>
          ))}
        </div>
      </div>

      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="p-6 space-y-4">
          {/* Step 0: Campaign Details */}
          {step === 0 && (
            <div className="space-y-4 fade-in">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="sm:col-span-2">
                  <label className="label-base">Campaign Name</label>
                  <input
                    type="text"
                    placeholder="e.g. Summer Sale — Hero Banner"
                    className={`input-base ${errors.name ? 'border-negative' : ''}`}
                    {...register('name', { required: 'Campaign name is required' })}
                  />
                  {errors.name && <p className="error-text">{errors.name.message}</p>}
                </div>
                <div>
                  <label className="label-base">Client Account</label>
                  <select className={`input-base ${errors.client ? 'border-negative' : ''}`} {...register('client', { required: 'Select a client' })}>
                    <option value="">Select client…</option>
                    {clients.map((c) => <option key={`opt-client-${c}`} value={c}>{c}</option>)}
                  </select>
                  {errors.client && <p className="error-text">{errors.client.message}</p>}
                </div>
                <div>
                  <label className="label-base">Campaign Objective</label>
                  <select className="input-base" {...register('objective', { required: true })}>
                    <option value="">Select objective…</option>
                    <option value="Conversions">Conversions</option>
                    <option value="Lead Generation">Lead Generation</option>
                    <option value="Brand Awareness">Brand Awareness</option>
                    <option value="Traffic">Traffic</option>
                    <option value="App Installs">App Installs</option>
                    <option value="Engagement">Engagement</option>
                    <option value="Video Views">Video Views</option>
                  </select>
                </div>
                <div>
                  <label className="label-base">Platform</label>
                  <select className="input-base" {...register('platform')}>
                    <option value="both">Facebook & Instagram</option>
                    <option value="facebook">Facebook only</option>
                    <option value="instagram">Instagram only</option>
                  </select>
                </div>
                <div>
                  <label className="label-base">Budget Type</label>
                  <select className="input-base" {...register('budgetType')}>
                    <option value="daily">Daily Budget</option>
                    <option value="lifetime">Lifetime Budget</option>
                  </select>
                </div>
                <div>
                  <label className="label-base">Budget Amount (USD)</label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">$</span>
                    <input
                      type="number"
                      placeholder="500"
                      className={`input-base pl-7 ${errors.budget ? 'border-negative' : ''}`}
                      {...register('budget', { required: 'Budget is required', min: { value: 1, message: 'Minimum $1' } })}
                    />
                  </div>
                  {errors.budget && <p className="error-text">{errors.budget.message}</p>}
                </div>
                <div>
                  <label className="label-base">Start Date</label>
                  <input type="date" className="input-base" {...register('startDate', { required: true })} />
                </div>
                <div>
                  <label className="label-base">End Date</label>
                  <input type="date" className="input-base" {...register('endDate', { required: true })} />
                </div>
              </div>
            </div>
          )}

          {/* Step 1: Targeting */}
          {step === 1 && (
            <div className="space-y-4 fade-in">
              <div className="p-3 bg-primary/5 rounded-lg border border-primary/20">
                <p className="text-xs font-medium text-primary">Ad Set: Default Ad Set</p>
                <p className="text-xs text-muted-foreground mt-0.5">You can add more ad sets after creating the campaign</p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="label-base">Age Range</label>
                  <select className="input-base" {...register('targetAge')}>
                    <option value="18-65+">18–65+ (All adults)</option>
                    <option value="18-24">18–24</option>
                    <option value="25-34">25–34</option>
                    <option value="35-44">35–44</option>
                    <option value="45-54">45–54</option>
                    <option value="55-65+">55–65+</option>
                    <option value="custom">Custom range</option>
                  </select>
                </div>
                <div>
                  <label className="label-base">Gender</label>
                  <select className="input-base" {...register('targetGender')}>
                    <option value="all">All genders</option>
                    <option value="male">Male</option>
                    <option value="female">Female</option>
                  </select>
                </div>
                <div className="sm:col-span-2">
                  <label className="label-base">Target Locations</label>
                  <p className="helper-text">Enter cities, states, or countries separated by commas</p>
                  <input
                    type="text"
                    placeholder="e.g. New York, Los Angeles, Chicago"
                    className="input-base"
                    {...register('targetLocations')}
                  />
                </div>
                <div className="sm:col-span-2">
                  <label className="label-base">Interest Targeting</label>
                  <p className="helper-text">Enter interests separated by commas to narrow your audience</p>
                  <input
                    type="text"
                    placeholder="e.g. fitness, healthy eating, yoga, wellness"
                    className="input-base"
                    {...register('targetInterests')}
                  />
                </div>
              </div>
              <div className="grid grid-cols-3 gap-3">
                {['Broad Audience', 'Lookalike 1%', 'Retargeting'].map((audience) => (
                  <button
                    key={`audience-${audience}`}
                    type="button"
                    className="p-3 rounded-lg border border-border hover:border-primary/40 hover:bg-primary/5 text-xs font-medium text-foreground text-left transition-all duration-150"
                  >
                    <p className="font-semibold">{audience}</p>
                    <p className="text-muted-foreground mt-0.5">
                      {audience === 'Broad Audience' ? 'Max reach, lower CPM' : audience === 'Lookalike 1%' ? 'Similar to best customers' : 'Website / app visitors'}
                    </p>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Step 2: Creative */}
          {step === 2 && (
            <div className="space-y-4 fade-in">
              <div>
                <label className="label-base">Ad Copy (Primary Text)</label>
                <p className="helper-text">Appears above the image/video. Max 125 characters recommended.</p>
                <textarea
                  rows={3}
                  placeholder="Write compelling ad copy that speaks directly to your audience's needs…"
                  className="input-base resize-none"
                  {...register('adCopy', { required: 'Ad copy is required' })}
                />
                {errors.adCopy && <p className="error-text">{errors.adCopy.message}</p>}
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="label-base">Call-to-Action Button</label>
                  <select className="input-base" {...register('ctaButton')}>
                    <option value="Shop Now">Shop Now</option>
                    <option value="Learn More">Learn More</option>
                    <option value="Sign Up">Sign Up</option>
                    <option value="Get Offer">Get Offer</option>
                    <option value="Book Now">Book Now</option>
                    <option value="Download">Download</option>
                    <option value="Contact Us">Contact Us</option>
                    <option value="Watch More">Watch More</option>
                  </select>
                </div>
                <div>
                  <label className="label-base">Destination URL</label>
                  <input
                    type="url"
                    placeholder="https://yoursite.com/landing-page"
                    className="input-base"
                    {...register('destinationUrl', { required: 'Destination URL is required' })}
                  />
                  {errors.destinationUrl && <p className="error-text">{errors.destinationUrl.message}</p>}
                </div>
              </div>
              {/* Media Upload Placeholder */}
              <div>
                <label className="label-base">Ad Creative (Image or Video)</label>
                <p className="helper-text">Recommended: 1200×628px for Feed, 1080×1920px for Stories. Max 30MB.</p>
                <div className="mt-1 border-2 border-dashed border-border rounded-xl p-8 text-center hover:border-primary/40 hover:bg-primary/[0.02] transition-all duration-150 cursor-pointer">
                  <div className="w-10 h-10 rounded-xl bg-muted flex items-center justify-center mx-auto mb-3">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="text-muted-foreground">
                      <rect x="3" y="3" width="18" height="18" rx="2" /><circle cx="8.5" cy="8.5" r="1.5" /><path d="m21 15-5-5L5 21" />
                    </svg>
                  </div>
                  <p className="text-sm font-medium text-foreground">Drop files here or click to upload</p>
                  <p className="text-xs text-muted-foreground mt-1">JPG, PNG, MP4, MOV — max 30MB</p>
                  {/* Backend integration point: file upload to media storage (S3 or similar) */}
                </div>
              </div>
            </div>
          )}

          {/* Step 3: Review */}
          {step === 3 && (
            <div className="space-y-4 fade-in">
              <div className="p-4 rounded-xl bg-muted/50 space-y-3">
                <h4 className="text-sm font-semibold text-foreground">Campaign Summary</h4>
                <div className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm">
                  <div>
                    <p className="text-xs text-muted-foreground">Campaign Name</p>
                    <p className="font-medium text-foreground">{watchedValues.name || '—'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Client</p>
                    <p className="font-medium text-foreground">{watchedValues.client || '—'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Objective</p>
                    <p className="font-medium text-foreground">{watchedValues.objective || '—'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Platform</p>
                    <p className="font-medium text-foreground capitalize">{watchedValues.platform || '—'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Budget</p>
                    <p className="font-medium text-foreground">${watchedValues.budget || '—'} / {watchedValues.budgetType}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Duration</p>
                    <p className="font-medium text-foreground">{watchedValues.startDate || '—'} → {watchedValues.endDate || '—'}</p>
                  </div>
                </div>
              </div>
              <div className="p-3 rounded-xl border border-warning/30 bg-warning/5 flex items-start gap-3">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-warning shrink-0 mt-0.5">
                  <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/>
                </svg>
                <div>
                  <p className="text-xs font-semibold text-warning">Submitted for review</p>
                  <p className="text-xs text-muted-foreground mt-0.5">This campaign will be reviewed by a manager before going live. Typical review time: 2–4 hours.</p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-6 py-4 border-t border-border">
          <button
            type="button"
            onClick={step === 0 ? onClose : prevStep}
            className="btn-ghost"
          >
            {step === 0 ? 'Cancel' : '← Back'}
          </button>
          {step < steps.length - 1 ? (
            <button type="button" onClick={nextStep} className="btn-primary">
              Continue →
            </button>
          ) : (
            <button type="submit" disabled={isSubmitting} className="btn-primary">
              {isSubmitting ? (
                <span className="flex items-center gap-2">
                  <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                  Submitting…
                </span>
              ) : (
                'Submit for Review'
              )}
            </button>
          )}
        </div>
      </form>
    </Modal>
  );
}